// src/components/results/ResultsStory.jsx
//
// The results page as a corrected exam, in tabs: the Overview first (verdict, score,
// the teacher's comment, where the points are, one card per part), then one tab per
// exam part with its answer sheet, traps and next steps, and Schreiben with the
// corrected text. Sprechen is shown as "coming soon". "Review my mistakes" opens as a
// full-screen view, for the whole paper or one part.
// All numbers come from the page's saved result and from resultInsights; nothing is
// invented. Design: the red-pen canvas (v2) and doc/RESULTS_TAG_TAXONOMY.md.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Lock } from 'lucide-react';
import { getExamSlug } from '../../utils/examSlug';
import { getGoetheModuleSummary } from '../../utils/goetheExamDisplay';
import { useCaveat } from '../mockExam/RedPenCards';
import { useLocalizedNavigate } from '../../hooks/useLocalizedNavigate';
import ReviewMode from './ReviewMode';
import PartTab from './PartTab';
import WritingTab from './WritingTab';
import { familyMissPct, rankOf, useResultNorms } from './peerText';
import { causeGroup, familyEntry, fmt, indexTaxonomy, inkPath, itemKey, labelOf, pick, questionText, sectionScore } from './storyText';

const HAND = { fontFamily: "'Caveat', cursive" };
const DISPLAY = { fontFamily: "'Space Grotesk', sans-serif" };
const ITEM_SECTIONS = [['lesen', 'Lesen'], ['sprachbausteine', 'Sprachbausteine'], ['hoeren', 'Hören']];
const SECTION_NAME = { lesen: 'Lesen', sprachbausteine: 'Sprachbausteine', hoeren: 'Hören', schreiben: 'Schreiben' };

export default function ResultsStory({
  exam, results, insights, taxonomy, passed, passPct = 60, isCorrecting, writingSlot, writingCorrections, writingScore,
  formatAnswer, isFree, onLocked, onExit, previous, retrySet,
}) {
  const { t, i18n } = useTranslation();
  useCaveat();
  const lang = (i18n.language || 'en').split('-')[0];
  const navigate = useLocalizedNavigate();
  const idx = useMemo(() => indexTaxonomy(taxonomy), [taxonomy]);
  const [tab, setTab] = useState('overview');
  const [review, setReview] = useState(null);   // null | 'all' | section key
  const tabsRef = useRef(null);
  const norms = useResultNorms(exam.examProvider, exam.level);
  const panelRef = useRef(null);

  // question + part lookup for every item
  const qIndex = useMemo(() => {
    const m = {};
    (exam.sections || []).forEach((s) => (s.parts || []).forEach((p) => (p.questions || []).forEach((q) => { m[itemKey({ partId: p.partId, questionId: q.questionId })] = { q, part: p, section: s }; })));
    return m;
  }, [exam]);
  const whereOf = (item) => `${SECTION_NAME[item.section]} · ${t('examResults.story.teil', { n: qIndex[itemKey(item)].part.partNumber })} · ${item.number ?? ''}`;
  const openPractice = (link) => (link.state ? navigate(link.to, { state: link.state }) : navigate(link.to));
  const hasFlags = results.examMode !== 'paper';

  const score = Number(results.overallScore ?? 0);
  const max = Number(results.maxScore ?? 225);
  const passMark = Math.round(max * passPct / 100);
  const above = score - passMark;
  // Goethe has no total: each module (Lesen, Hören, Schreiben) is passed on its own.
  const modules = getGoetheModuleSummary(results);
  const rank = isCorrecting || modules ? null : rankOf(norms, score / max);

  // the parts this paper has, with the page's authoritative scores
  const parts = useMemo(() => {
    const list = ITEM_SECTIONS
      .filter(([key]) => insights.items.some((it) => it.section === key))
      .map(([key, name]) => {
        const sec = insights.sections.find((s) => s.id === key);
        return { key, name, score: sectionScore(results.sectionScores, key) || { score: sec?.points ?? 0, max: sec?.max ?? 0 }, items: insights.items.filter((it) => it.section === key) };
      });
    const fromResults = sectionScore(results.sectionScores, 'schreiben');
    const w = fromResults && fromResults.score > 0 ? fromResults : (writingScore || fromResults);
    if (writingSlot || (w && w.max)) list.push({ key: 'schreiben', name: SECTION_NAME.schreiben, score: w, items: [] });
    return list;
  }, [insights, results.sectionScores, writingSlot, writingScore]);

  const switchTab = (key) => {
    setTab(key);
    document.getElementById(`rs-tab-${key}`)?.scrollIntoView({ inline: 'nearest', block: 'nearest' });
    // When the reader is already down in the content, start the new tab at its top,
    // right under the sticky tab bar.
    const nav = tabsRef.current; const panel = panelRef.current;
    if (nav && panel && panel.getBoundingClientRect().top < nav.getBoundingClientRect().bottom - 1) {
      panel.style.scrollMarginTop = `${nav.offsetHeight + 16}px`;
      panel.scrollIntoView({ block: 'start' });
    }
  };
  const tabOfFamily = (f) => (f.sections || []).find((s) => parts.some((p) => p.key === s)) || 'overview';

  // first screen: teacher's comment, strength, focus areas
  const bestSection = useMemo(() => [...insights.sections].sort((a, b) => b.ratio - a.ratio)[0], [insights]);
  const topFocus = insights.focus[0];
  const familyLabel = (f) => (f.kind === 'section' ? SECTION_NAME[f.id] : labelOf(idx, f.kind, f.id, lang));
  const comment = topFocus
    ? t(passed ? 'examResults.story.comment' : 'examResults.story.commentNotYet', { strong: SECTION_NAME[bestSection?.id] || '', focus: familyLabel(topFocus) })
    : t('examResults.story.commentClean', { strong: SECTION_NAME[bestSection?.id] || '' });
  const strength = insights.strengths[0];
  const strengthText = strength
    ? (strength.kind === 'section'
      ? t('examResults.story.strongSection', { section: SECTION_NAME[strength.id] })
      : pick(familyEntry(idx, strength.kind, strength.id)?.praise, lang))
    : '';

  // score count-up
  const [shownScore, setShownScore] = useState(0);
  useEffect(() => {
    if (isCorrecting) return undefined;
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) { setShownScore(score); return undefined; }
    let raf; let start;
    const tick = (now) => {
      start = start ?? now;
      const p = Math.min(1, (now - start) / 1400);
      setShownScore(Math.round(score * (1 - (1 - p) ** 3)));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    const timer = setTimeout(() => { raf = requestAnimationFrame(tick); }, 300);
    return () => { clearTimeout(timer); cancelAnimationFrame(raf); };
  }, [score, isCorrecting]);

  // one line per part card: where the points went, from real counts
  const partInsight = (p) => {
    if (p.key === 'schreiben') {
      const fams = {};
      (writingCorrections || []).forEach((c) => { const f = c?.tag && idx.point[c.tag]?.family; if (f) fams[f] = (fams[f] || 0) + 1; });
      const [fam, n] = Object.entries(fams).sort((a, b) => b[1] - a[1])[0] || [];
      if (isCorrecting) return t('examResults.story.overview.writingWaiting');
      return fam ? t('examResults.story.overview.writingTop', { what: labelOf(idx, 'language', fam, lang), count: n }) : t('examResults.story.overview.writingOpen');
    }
    const wrong = p.items.filter((it) => !it.correct);
    const groups = {};
    wrong.forEach((it) => {
      const g = causeGroup(it, idx, lang, t);
      if (g?.label) { groups[g.key] = groups[g.key] || { ...g, count: 0, lost: 0 }; groups[g.key].count += 1; groups[g.key].lost += it.lost; }
    });
    const top = Object.values(groups).sort((a, b) => (b.count - a.count) || (b.lost - a.lost))[0];
    const best = bestSection?.id === p.key ? `${t('examResults.story.overview.partBest')} ` : '';
    if (!wrong.length) return t('examResults.story.overview.partClean');
    if (top && top.count > 1) return best + t('examResults.story.overview.partTrap', { count: top.count, what: top.label, points: fmt(top.lost) });
    return best + t('examResults.story.overview.partFew', { count: wrong.length });
  };

  // since last attempt: the total, and the part that grew most
  const since = useMemo(() => {
    if (!previous?.sectionScores) return null;
    const diff = modules ? null : Math.round(score - Number(previous.overallScore ?? 0));
    let grew = null;
    parts.forEach((p) => {
      const before = sectionScore(previous.sectionScores, p.key);
      if (!before?.max || !p.score?.max) return;
      const d = Math.round((p.score.score / p.score.max - before.score / before.max) * 100);
      if (d > 0 && (!grew || d > grew.d)) grew = { name: p.name, d };
    });
    return diff == null && !grew ? null : { diff, grew };
  }, [previous, parts, score, modules]);

  // Goethe: the module closest to its pass mark among those not yet passed
  const moduleGap = modules ? parts
    .filter((p) => p.score?.max && p.score.score < p.score.max * passPct / 100)
    .map((p) => ({ name: p.name, gap: p.score.max * passPct / 100 - p.score.score }))
    .sort((a, b) => a.gap - b.gap)[0] : null;

  const tabs = [
    { key: 'overview', name: t('examResults.story.overview.tab'), score: isCorrecting ? '…' : (modules ? modules.displayShort : `${fmt(score)} / ${max}`) },
    ...parts.map((p) => ({ key: p.key, name: p.name, score: p.score && !(p.key === 'schreiben' && isCorrecting) ? `${fmt(p.score.score)} / ${fmt(p.score.max)}` : '…' })),
  ];
  const current = parts.find((p) => p.key === tab);
  const retry = retrySet ? (() => {
    const dueMs = retrySet.dueAt?.toMillis ? retrySet.dueAt.toMillis() : Number(retrySet.dueAt);
    return {
      due: dueMs <= Date.now(),
      count: (retrySet.items || []).filter((it) => !it.done).length,
      date: new Date(dueMs).toLocaleDateString(i18n.language, { weekday: 'short', day: 'numeric', month: 'short' }),
      to: `/exams/${getExamSlug(exam.examProvider, exam.level)}/retry/${retrySet.id}`,
    };
  })() : null;

  return (
    <div className="min-h-screen bg-[#FAF8F3] text-stone-900">
      <div className="max-w-[1100px] mx-auto px-4 sm:px-6 pt-6 sm:pt-7 pb-32 flex flex-col gap-8 sm:gap-9">
        <header className="flex flex-col gap-2.5">
          <button type="button" onClick={onExit} className="rs-focus self-start min-h-[44px] inline-flex items-center gap-1.5 text-sm font-semibold text-stone-900">
            <ArrowLeft className="w-4 h-4" aria-hidden="true" />{t('examResults.backToDashboard')}
          </button>
          <p className="m-0 text-[13px] font-semibold tracking-[.08em] uppercase text-stone-600">
            {exam.examName || [exam.examProvider, exam.level].filter(Boolean).join(' ')}
          </p>
          <h1 className="m-0 text-[30px] sm:text-[44px] leading-[1.05] font-bold tracking-tight" style={DISPLAY}>{t('examResults.story.title')}</h1>
        </header>

        {/* Tabs: Overview, one per part, Sprechen coming soon */}
        <nav ref={tabsRef} aria-label={t('examResults.story.overview.tabsLabel')}
          className="rs-tabs sticky top-0 z-[6] bg-[#FAF8F3] flex gap-1.5 overflow-x-auto overflow-y-hidden pt-2 px-1 -mx-1 border-b-2 border-stone-900">
          <div role="tablist" className="flex gap-1.5">
            {tabs.map((tb) => {
              const on = tb.key === tab;
              return (
                <button key={tb.key} type="button" role="tab" id={`rs-tab-${tb.key}`} aria-selected={on} aria-controls="rs-tabpanel" onClick={() => switchTab(tb.key)}
                  className={`rs-tab rs-focus shrink-0 min-h-[52px] min-w-[112px] px-4 pt-2.5 pb-2 -mb-0.5 rounded-t-lg border-2 border-b-0 bg-white flex flex-col items-start gap-0.5 text-left touch-manipulation ${on ? 'border-stone-900 text-stone-900 shadow-[inset_0_-4px_0_#DC2626]' : 'border-stone-300 text-stone-600'}`}>
                  <span className="font-bold text-[15px]">{tb.name}</span>
                  <span className={`text-xl leading-none ${on ? 'text-red-600' : 'text-stone-500'}`} style={HAND}>{tb.score}</span>
                </button>
              );
            })}
          </div>
          <span aria-disabled="true" className="shrink-0 min-h-[52px] min-w-[112px] px-4 pt-2.5 pb-2 -mb-0.5 rounded-t-lg border-2 border-b-0 border-dashed border-stone-400 flex flex-col gap-0.5 text-stone-500">
            <span className="font-bold text-[15px]">Sprechen</span>
            <span className="text-xl leading-none" style={HAND}>{t('examResults.story.overview.comingSoon')}</span>
          </span>
        </nav>

        <div ref={panelRef} id="rs-tabpanel" role="tabpanel" aria-labelledby={`rs-tab-${tab}`}>
          {tab === 'overview' && (
            <div className="flex flex-col gap-10">
              {/* Verdict + where the points are */}
              <section className="rs-up grid gap-6 [grid-template-columns:repeat(auto-fit,minmax(min(100%,380px),1fr))]">
                <div className="rs-paper flex flex-col gap-4 px-6 sm:px-7 pt-7 pb-8" style={{ borderTop: '6px solid #1C1917', backgroundPosition: '0 92px' }}>
                  {isCorrecting ? (
                    <div className="flex flex-col gap-2 py-6">
                      <span className="text-2xl font-bold" style={DISPLAY}>{t('examResults.evaluatingTitle', 'Auswertung läuft')}</span>
                      <span className="text-stone-600">{t('examResults.story.waitingForWriting')}</span>
                    </div>
                  ) : (
                    <>
                      <div className="relative self-start px-4 py-1.5">
                        <span className="text-[22px] font-bold tracking-[.14em]" style={{ ...DISPLAY, color: passed ? '#DC2626' : '#44403C' }}>
                          {passed ? t('examResults.story.passedStamp') : t('examResults.story.notYetStamp')}
                        </span>
                        <svg viewBox="0 0 160 56" className="absolute -inset-x-3.5 -inset-y-2 w-[calc(100%+28px)] h-[calc(100%+16px)] overflow-visible" aria-hidden="true">
                          <path className="rs-stroke rs-draw" pathLength="1" stroke={passed ? '#DC2626' : '#78716C'} strokeWidth="2.6" style={{ animationDelay: '.5s' }} d="M18 30 C 10 8, 140 2, 150 24 C 158 44, 40 56, 14 40 C 4 32, 30 14, 60 10" />
                        </svg>
                      </div>
                      {modules ? (
                        <>
                          <div className="flex items-baseline gap-2.5">
                            <span className="text-[64px] sm:text-[84px] leading-[.9] font-bold tracking-[-0.04em]" style={DISPLAY}>{modules.passed}</span>
                            <span className="text-[22px] font-semibold text-stone-600">/ {modules.total} {t('examResults.story.goethe.modules')}</span>
                          </div>
                          <ul className="m-0 p-0 list-none flex flex-col gap-3">
                            {parts.filter((p) => p.score?.max).map((p, i) => {
                              const ratio = p.score.score / p.score.max;
                              const ok = ratio >= passPct / 100;
                              return (
                                <li key={p.key} className="grid grid-cols-[86px_minmax(0,1fr)_72px] items-center gap-3">
                                  <span className="font-semibold">{p.name}</span>
                                  <svg viewBox="0 0 260 30" className="w-full h-auto overflow-visible" role="img" aria-label={`${fmt(p.score.score)} / ${fmt(p.score.max)}`}>
                                    <line x1="0" y1="16" x2="250" y2="16" stroke="#E7E5E4" strokeWidth="2" />
                                    <path className="rs-stroke rs-draw" pathLength="1" stroke="#1C1917" strokeWidth="6" d={inkPath(ratio, 250, 16, 25)} style={{ animationDelay: `${0.3 + i * 0.2}s`, animationDuration: '1.1s' }} />
                                    <line x1={passPct * 2.5} y1="3" x2={passPct * 2.5} y2="29" stroke="#DC2626" strokeWidth="2" strokeDasharray="4 4" />
                                  </svg>
                                  <span className={`text-[24px] leading-none text-right whitespace-nowrap ${ok ? 'text-[#006D77]' : 'text-red-600'}`} style={HAND}>
                                    {fmt(p.score.score)} {ok ? '✓' : '✗'}
                                  </span>
                                </li>
                              );
                            })}
                          </ul>
                          <p className="m-0 text-[13px] text-stone-600">{t('examResults.story.goethe.rule', { pct: passPct })}</p>
                          <p className="m-0 text-[17px] font-semibold">
                            {moduleGap ? t('examResults.story.goethe.toPass', { n: fmt(moduleGap.gap), name: moduleGap.name }) : t('examResults.story.goethe.allPassed')}
                          </p>
                        </>
                      ) : (<>
                      <div className="flex items-baseline gap-2.5">
                        <span className="text-[64px] sm:text-[84px] leading-[.9] font-bold tracking-[-0.04em]" style={DISPLAY}>{fmt(shownScore)}</span>
                        <span className="text-[22px] font-semibold text-stone-600">/ {max}</span>
                      </div>
                      <div className="flex flex-col gap-2">
                        <div className="relative h-3.5 bg-stone-200 rounded-full">
                          <div className="h-full bg-stone-900 rounded-full transition-[width] duration-[1400ms] ease-out" style={{ width: `${Math.min(100, (shownScore / max) * 100)}%` }} />
                          <div className="absolute -top-1.5 -bottom-1.5 w-0.5 bg-red-600" style={{ left: `${passPct}%` }} />
                        </div>
                        <div className="flex justify-between text-[13px] text-stone-600">
                          <span>0</span><span className="font-semibold text-red-700">{t('examResults.story.passMark', { points: passMark, pct: passPct })}</span><span>{max}</span>
                        </div>
                      </div>
                      <p className="m-0 text-[17px] font-semibold">
                        {above >= 0 ? t('examResults.story.aboveMark', { n: fmt(above) }) : t('examResults.story.belowMark', { n: fmt(-above) })}
                      </p>
                      </>)}
                      {rank && (
                        <p className="rs-later m-0 -mt-1 text-[15px] text-stone-700" style={{ animationDelay: '1.6s' }}>
                          <span className="text-[26px] leading-none text-[#006D77]" style={HAND}>{t('examResults.story.peer.rankHand', { pct: rank.pct })}</span>{' '}
                          {t('examResults.story.peer.rankText', { students: rank.students, exam: [exam.examProvider, exam.level].filter(Boolean).join(' ') })}
                        </p>
                      )}
                      <p className="m-0 mt-1 text-[28px] sm:text-[30px] leading-[1.15] text-red-600" style={HAND}>
                        <span className="redpen-note" style={{ animationDelay: '1.2s' }}>{comment}</span>
                      </p>
                    </>
                  )}
                </div>

                <div className="rs-paper flex flex-col px-6 pt-5 pb-6" style={{ backgroundPosition: '0 20px' }}>
                  <h2 className="m-0 mb-1.5 text-[34px] leading-[1.1] text-red-600 -rotate-[1.5deg] origin-left font-semibold" style={HAND}>{t('examResults.story.gainTitle')}</h2>
                  {strengthText && (
                    <div className="flex items-baseline gap-3 pt-3 pb-3.5 border-b border-dashed border-stone-300">
                      <span className="relative text-[28px] leading-none text-[#006D77] whitespace-nowrap" style={HAND}>{t('examResults.story.strong')}
                        <svg viewBox="0 0 100 10" className="absolute left-0 -bottom-1.5 w-full h-2 overflow-visible" aria-hidden="true">
                          <path className="rs-stroke rs-draw" pathLength="1" stroke="#006D77" strokeWidth="2.6" style={{ animationDelay: '1.4s' }} d="M2 6 C 30 2, 70 8, 98 4" />
                        </svg>
                      </span>
                      <span className="font-semibold leading-snug">{strengthText}</span>
                    </div>
                  )}
                  {insights.focus.length === 0 && <p className="m-0 py-4 text-stone-700">{t('examResults.story.noFocus')}</p>}
                  {insights.focus.map((f, i) => (
                    <button key={f.key} type="button" onClick={() => switchTab(tabOfFamily(f))} className="rs-row rs-focus text-left grid grid-cols-[22px_minmax(0,1fr)_78px] sm:grid-cols-[28px_minmax(0,1fr)_92px] gap-3 items-center py-3.5 border-b border-dashed border-stone-300 min-h-[56px] touch-manipulation">
                      <span className="font-bold text-[15px] text-stone-500" style={DISPLAY}>{i + 1}</span>
                      <span className="flex flex-col gap-1 min-w-0">
                        <span className="font-bold text-lg text-stone-900">{familyLabel(f)}</span>
                        <span className="text-sm text-stone-600">
                          {t('examResults.story.focusSub', { wrong: f.wrong, items: f.items, sections: f.sections.map((s) => SECTION_NAME[s]).join(' + ') })}
                        </span>
                        {familyMissPct(norms, f.key) != null && (
                          <span className="text-sm text-[#006D77]">{t('examResults.story.peer.family', { pct: familyMissPct(norms, f.key) })}</span>
                        )}
                        <span className="text-sm font-semibold text-red-700">{t('examResults.story.seeWhy')} →</span>
                      </span>
                      <span className="relative justify-self-center px-2.5 py-1.5 text-[38px] leading-none text-red-600 whitespace-nowrap" style={HAND}>
                        {f.lost > 0 ? `+${fmt(f.lost)}` : `${f.wrong}×`}
                        <svg viewBox="0 0 100 60" className="absolute -inset-x-2 -inset-y-1.5 w-[calc(100%+16px)] h-[calc(100%+12px)] overflow-visible" aria-hidden="true">
                          <path className="rs-stroke rs-draw" pathLength="1" stroke="#DC2626" strokeWidth="2.6" style={{ animationDelay: `${1.7 + i * 0.35}s` }} d="M14 34 C 6 12, 80 2, 92 24 C 100 44, 34 60, 10 42 C 4 36, 18 22, 40 18" />
                        </svg>
                      </span>
                    </button>
                  ))}
                  {insights.focus.length > 0 && <span className="mt-2.5 text-[13px] text-stone-600">{t('examResults.story.gainNote')}</span>}
                </div>
              </section>

              {/* One card per part */}
              <section className="flex flex-col gap-4">
                <div>
                  <h2 className="m-0 text-[26px] sm:text-[28px] font-bold" style={DISPLAY}>{t('examResults.story.overview.partsTitle', { count: parts.length })}</h2>
                  <p className="m-0 mt-1.5 text-stone-600">{t('examResults.story.overview.partsIntro')}</p>
                </div>
                <div className="grid gap-[18px] [grid-template-columns:repeat(auto-fit,minmax(min(100%,240px),1fr))]">
                  {parts.map((p, i) => {
                    const ratio = p.score?.max ? p.score.score / p.score.max : 0;
                    const weak = p.score?.max && ratio < passPct / 100;
                    return (
                      <button key={p.key} type="button" onClick={() => switchTab(p.key)}
                        className="rs-lift rs-up rs-focus group text-left bg-white rounded-md px-5 pt-[18px] pb-5 flex flex-col gap-2.5 min-h-[200px] shadow-[0_18px_40px_-30px_rgba(28,25,23,.45)] touch-manipulation"
                        style={{ borderTop: `6px solid ${weak ? '#DC2626' : '#1C1917'}`, animationDelay: `${0.2 + i * 0.15}s` }}>
                        <span className="flex flex-wrap justify-between items-baseline gap-x-2 w-full">
                          <span className="font-bold text-[21px]" style={DISPLAY}>{p.name}</span>
                          <span className="text-[30px] leading-none whitespace-nowrap" style={HAND}>{p.score && !(p.key === 'schreiben' && isCorrecting) ? `${fmt(p.score.score)} / ${fmt(p.score.max)}` : '…'}</span>
                        </span>
                        <svg viewBox="0 0 260 30" className="w-full h-auto overflow-visible" aria-hidden="true">
                          <line x1="0" y1="16" x2="250" y2="16" stroke="#E7E5E4" strokeWidth="2" />
                          {p.score?.max > 0 && !(p.key === 'schreiben' && isCorrecting) && (
                            <path className="rs-stroke rs-draw" pathLength="1" stroke="#1C1917" strokeWidth="6" d={inkPath(ratio, 250, 16, 25)} style={{ animationDelay: `${0.2 + i * 0.15}s`, animationDuration: '1.1s' }} />
                          )}
                          <line x1={passPct * 2.5} y1="3" x2={passPct * 2.5} y2="29" stroke="#DC2626" strokeWidth="2" strokeDasharray="4 4" />
                        </svg>
                        <span className="text-sm leading-relaxed text-stone-700">{partInsight(p)}</span>
                        <span className="mt-auto text-sm font-bold text-red-700">{t('examResults.story.overview.openPart', { name: p.name })} →</span>
                      </button>
                    );
                  })}
                </div>
              </section>

              {/* Your mistakes, ready to learn from */}
              {insights.counts.wrong > 0 && (
                <section className="rs-up grid gap-6 items-center bg-stone-900 text-white rounded-xl p-6 sm:p-7 [grid-template-columns:repeat(auto-fit,minmax(min(100%,360px),1fr))]">
                  <div className="flex flex-col gap-2.5">
                    <span className="text-[30px] leading-tight text-red-300 -rotate-[1.5deg] origin-left" style={HAND}>
                      {retry ? t(retry.due ? 'examResults.story.retry.kickerReady' : 'examResults.story.retry.kickerOn', { date: retry.date }) : t('examResults.story.overview.reviewKicker')}
                    </span>
                    <h2 className="m-0 text-2xl sm:text-[26px] leading-tight font-bold" style={DISPLAY}>{t('examResults.story.closeTitle', { count: insights.counts.wrong })}</h2>
                    <p className="m-0 text-stone-300 leading-relaxed">{hasFlags ? t('examResults.story.closeText') : t('examResults.story.reviewIntroPaper')}</p>
                    <div className="flex flex-wrap gap-3 mt-1.5">
                      <button type="button" onClick={() => setReview('all')} className="rs-focus min-h-[48px] px-5 rounded-[10px] bg-white text-stone-900 font-bold touch-manipulation active:scale-[0.98] transition-transform">
                        {t('examResults.story.overview.reviewAll', { count: insights.counts.wrong })} →
                      </button>
                      {retry && (isFree || retry.due) && (
                        <button type="button" onClick={() => (isFree ? onLocked() : navigate(retry.to))}
                          className="rs-focus min-h-[48px] px-5 rounded-[10px] border-2 border-stone-500 text-white font-semibold inline-flex items-center gap-2 touch-manipulation active:scale-[0.98] transition-transform">
                          {isFree && <Lock className="w-4 h-4" aria-hidden="true" />}
                          {isFree ? t('examResults.story.retry.unlock') : `${t('examResults.story.retry.start', { count: retry.count })} →`}
                        </button>
                      )}
                    </div>
                    {retry && <p className="m-0 text-sm text-stone-400">{t('examResults.story.retry.note', { count: retry.count, date: retry.date })}</p>}
                  </div>
                  <div aria-hidden="true" className="relative h-[230px]">
                    {insights.review.slice(0, 3).map((it, i) => {
                      const { q, part } = qIndex[itemKey(it)];
                      const text = questionText(q, part) ?? t('examResults.story.whichHeading', { n: q.questionNumber });
                      const pos = [['0', '18%', '0', '-2deg'], ['8%', '8%', '62px', '1.2deg'], ['16%', '0', '124px', '-0.8deg']][i];
                      return (
                        <div key={itemKey(it)} className="absolute bg-white text-stone-900 rounded-md px-4 py-3.5 shadow-[0_16px_30px_-20px_rgba(0,0,0,.6)]"
                          style={{ left: pos[0], right: pos[1], top: pos[2], transform: `rotate(${pos[3]})`, borderTop: '5px solid #DC2626' }}>
                          <p className="m-0 mb-1.5 text-xs font-bold text-stone-600">{whereOf(it)}</p>
                          <p className="m-0 font-semibold line-clamp-2">{text}</p>
                        </div>
                      );
                    })}
                  </div>
                </section>
              )}

              {since && (
                <section className="flex flex-wrap gap-3.5 items-center justify-between border-t border-dashed border-stone-300 pt-5">
                  <span className="text-[15px] text-stone-700">
                    <strong>{t('examResults.story.sinceTitle')}:</strong>{' '}
                    {since.diff != null && (
                      <span className="text-[26px]" style={{ ...HAND, color: since.diff >= 0 ? '#006D77' : '#B91C1C' }}>
                        {t('examResults.story.pointsDelta', { n: `${since.diff >= 0 ? '+' : '−'}${Math.abs(since.diff)}` })}
                      </span>
                    )}
                    {since.grew && <>{since.diff != null && ' · '}{t('examResults.story.overview.grewMost', { name: since.grew.name, n: since.grew.d })}</>}
                  </span>
                </section>
              )}
            </div>
          )}

          {current && current.key !== 'schreiben' && (
            <PartTab
              key={current.key}
              sectionKey={current.key} name={current.name} score={current.score} items={current.items}
              qIndex={qIndex} idx={idx} exam={exam} passPct={passPct} formatAnswer={formatAnswer}
              isFree={isFree} onPractice={openPractice} onLocked={onLocked} onReview={setReview} hasFlags={hasFlags} norms={norms}
            />
          )}

          {current?.key === 'schreiben' && (
            <WritingTab
              score={current.score} writingSlot={writingSlot} writingCorrections={writingCorrections}
              items={insights.items} idx={idx} exam={exam} isCorrecting={isCorrecting}
              isFree={isFree} onPractice={openPractice} onLocked={onLocked} onTab={switchTab}
            />
          )}
        </div>
      </div>

      {review && (
        <ReviewMode
          items={insights.review} qIndex={qIndex} whereOf={whereOf} idx={idx} exam={exam}
          formatAnswer={formatAnswer} isFree={isFree} onPractice={openPractice} onLocked={onLocked}
          hasFlags={hasFlags} initialFilter={review} norms={norms} onClose={() => setReview(null)}
        />
      )}
    </div>
  );
}
